/**
 * Pure scope-seed rules for the "Set up organization" wizard. Kept out of the
 * component so they are testable without a DOM, and so the SERVER's contract
 * is stated in exactly one place on this side of the wire.
 *
 * The name rules mirror vault-api's own (`scopeNamePattern`, `maxScopeNameLen`,
 * `reservedScopeNames`): `^[a-z][a-z0-9-]*$`, ≤63 chars, {default, shared}
 * reserved. They are deliberately STRICTER than CreateScopeDialog's client
 * pattern (which tolerates `/`): a slash in a wizard-seeded name is a path
 * mistaken for a grant key — the server refuses it, and by then the wizard's
 * rename has already happened, leaving the run half-finished.
 *
 * `metadata.path` is presentation only (the spec's §5.1: the grant key is
 * always the `name`). The SDK's `CreateScopeRequest` carries an arbitrary
 * metadata map, so the path travels there — verified against
 * node_modules/@cef-ai/vault-sdk/dist/internal/scopes.d.ts.
 */

/** One editable row of the wizard's scopes step. */
export interface ScopeSeedRow {
  name: string;
  path: string;
}

export const SCOPE_SEED_NAME_PATTERN = /^[a-z][a-z0-9-]*$/;
export const SCOPE_SEED_NAME_MAX = 63;
const RESERVED_SCOPE_NAMES = new Set(['default', 'shared']);

/** A wholly empty row is skipped by the run, not an error the user must fix. */
export function isSeedableRow(row: ScopeSeedRow): boolean {
  return row.name.trim() !== '' || row.path.trim() !== '';
}

/** Why the NAME cannot be a grant key, or null. Field-scoped so the UI can
 *  attribute it to the name input — a name problem rendered under the path
 *  field reads as a path problem. */
export function scopeSeedNameError(name: string): string | null {
  if (!name) return 'Enter a name.';
  if (name.length > SCOPE_SEED_NAME_MAX) return `At most ${SCOPE_SEED_NAME_MAX} characters.`;
  if (!SCOPE_SEED_NAME_PATTERN.test(name)) {
    return 'Lowercase letters, digits and hyphens only, starting with a letter. Paths belong in the path field.';
  }
  if (RESERVED_SCOPE_NAMES.has(name)) return '"default" and "shared" are reserved names.';
  return null;
}

/** Why the PATH cannot be stored as presentation metadata, or null. */
export function scopeSeedPathError(path: string): string | null {
  if (/\s/.test(path)) return 'Separate path segments with slashes — no spaces.';
  return null;
}

/** Why the row cannot be seeded, or null when it is valid (or empty). */
export function scopeSeedRowError(row: ScopeSeedRow): string | null {
  if (!isSeedableRow(row)) return null;
  return scopeSeedNameError(row.name.trim()) ?? scopeSeedPathError(row.path.trim());
}

/** The seedable rows in order; empty rows are dropped. */
export function seedableRows(rows: ScopeSeedRow[]): ScopeSeedRow[] {
  return rows.filter(isSeedableRow);
}

/**
 * Names used by more than one seedable row. Each duplicate would collide on
 * create — and the collision lands only AFTER the run has already renamed the
 * vault, which is exactly the half-finished state the client-side rules
 * exist to prevent. Uniqueness is a property of the row SET, so it cannot
 * live in `scopeSeedRowError`.
 */
export function duplicateSeedNames(rows: ScopeSeedRow[]): Set<string> {
  const seen = new Set<string>();
  const dups = new Set<string>();
  for (const row of rows) {
    if (!isSeedableRow(row)) continue;
    const name = row.name.trim();
    if (seen.has(name)) dups.add(name);
    seen.add(name);
  }
  return dups;
}

/**
 * The displayName derived from a name — mirrors CreateScopeDialog's
 * derivation so a scope seeded by the wizard is indistinguishable from one
 * created on the scopes page.
 */
export function deriveScopeDisplayName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return '';
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).replace(/[-_]+/g, ' ');
}

/** The shape handed to `signingHandle().scopes.create` for one seed row. */
export function toCreateRequest(row: ScopeSeedRow): {
  name: string;
  displayName: string;
  metadata?: { path: string };
} {
  const name = row.name.trim();
  const path = row.path.trim();
  return {
    name,
    displayName: deriveScopeDisplayName(name),
    ...(path ? { metadata: { path } } : {}),
  };
}
